import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import {
  AggregateGranularity,
  Hospital,
  HospitalAggregate,
  HospitalMatch,
  HospitalReport,
  HospitalStatus,
  HospitalV2,
  PatientDetails,
  ReportAttribution,
  SimpleHospitalReport,
} from './types';

type ResponseStatus = 'success';
interface ReportResponse {
  report_id: string;
  status: ResponseStatus;
}

interface HospitalResponse {
  hospital: HospitalV2;
  status: HospitalStatus;
}

interface HospitalsResponse {
  status: ResponseStatus;
  candidates: Array<Hospital>;
}

interface AggregateParams {
  level: AggregateGranularity;
  lat1?: number;
  lat2?: number;
  lng1?: number;
  lng2?: number;
}

interface AggregateResponse {
  [key: string]: HospitalAggregate;
}

const BASE_URL = process.env.VUE_APP_BASE_API_URL;

const authHandler = async (request: AxiosRequestConfig) => {
  const authEnabled = process.env.VUE_APP_AUTH0_ENABLED;
  if (authEnabled) {
    const auth = await import('@/util/auth');
    if (auth.isLoggedIn()) {
      request.headers['Authorization'] = `Bearer ${auth.getIdToken()}`;
    } else {
      auth.login();
    }
  }
  return Promise.resolve(request);
};
const authResponseHandler = async (error: AxiosError) => {
  if (403 === error?.response?.status) {
    const authEnabled = process.env.VUE_APP_AUTH0_ENABLED;
    if (authEnabled) {
      const auth = await import('@/util/auth');
      auth.login();
    }
  }
  return Promise.reject(error);
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

axiosInstance.interceptors.request.use(authHandler);
axiosInstance.interceptors.response.use(
  response => response,
  authResponseHandler,
);

export class Api {
  static async loadMatches(request: PatientDetails): Promise<HospitalMatch[]> {
    const response = await (
      await axiosInstance.post<HospitalsResponse>(
        `${BASE_URL}/incoming-patient`,
        request,
      )
    ).data;
    const { candidates, status } = response;
    if (candidates) {
      if (candidates.length > 0) {
        return candidates.map((item: any) => ({
          ...item,
          status: status[item.id] || null,
        }));
      } else {
        throw new Error(
          'We couldn’t find any matches at the moment. Please try again later.',
        );
      }
    } else {
      throw new Error(
        'There was an error getting matches. Please try again later.',
      );
    }
  }

  static async loadHospital(id: string): Promise<HospitalResponse> {
    const { data } = await axiosInstance.get(`${BASE_URL}/hospitals/${id}`);
    return data;
  }

  static async loadHospitals(): Promise<Hospital[]> {
    const { data } = await axiosInstance.get<HospitalsResponse>(
      `${BASE_URL}/hospitals`,
    );
    if (data.candidates) {
      //return [...data.candidates, ...data.candidates, ...data.candidates];
      return data.candidates;
    } else {
      throw new Error('Error response from API');
    }
  }

  static async submitReport(report: HospitalReport): Promise<ReportResponse> {
    const response = await axiosInstance.post(
      `${BASE_URL}/report/?v2=true`,
      report,
    );
    return response.data;
  }

  static async submitSimpleReport(
    report: SimpleHospitalReport,
  ): Promise<ReportResponse> {
    const response = await axiosInstance.post(
      `${BASE_URL}/simple-report/`,
      report,
    );
    return response.data;
  }

  static async attributeReport(
    attribution: ReportAttribution,
  ): Promise<ResponseStatus> {
    const response = await axiosInstance.post(
      `${BASE_URL}/attribute/`,
      attribution,
    );
    return response.data;
  }

  static async loadAggregateStatuses(
    level: AggregateGranularity = AggregateGranularity.STATE,
    bounds?: Array<Array<number>>,
  ): Promise<HospitalAggregate[]> {
    let params: AggregateParams = { level };
    if (bounds) {
      const [[lng1, lat1], [lng2, lat2]] = bounds;
      params = {
        level,
        lng1,
        lat1,
        lng2,
        lat2,
      };
    }
    const { data } = await axiosInstance.get<AggregateResponse>(
      `${BASE_URL}/aggregated-status`,
      { params },
    );
    return Object.values(data.aggregates);
  }
}

export default Api;
